<script setup lang="ts">
import { useMutation } from "@tanstack/vue-query";
import api from "~/utils/auth/api";
import { IOutcome } from "~/types/sports/outcome";
import t from "~/utils/translate";
import { IJackpotEvents, IOutcomeWithPrices } from "./Events.vue";
import Outcome from "./Outcome.vue";
import { IStrikeResponse } from "~/types/racing/jackpot-bets";
interface IPlaceModalProps {
	active: boolean;
	selectedLines: IOutcome[];
	jackpotEvents: IJackpotEvents[] | undefined;
	jackpotClosed: boolean | false;
}

const route = useRoute();
const props = defineProps<IPlaceModalProps>();
const emits = defineEmits(["set-active", "submit-confirmation"]);
const betsSelectedOutcomes = ref<IOutcome[]>();
const selectedMarketOutcome = ref<IOutcome>();

const jackpot = computed(() => {
	return { leagueId: route.query.jackpotName, date: route.query.jackpotDate };
});

const placeOutcomes = computed(() => {
	const placeSelectedLines: IOutcomeWithPrices[] = [];
	props.jackpotEvents?.forEach((event) => {
		const outcome = event.placeOutcomes.find((placeOutcome) =>
			props.selectedLines.some((winnerOutcome) => winnerOutcome.displayName === placeOutcome.displayName)
		);
		if (outcome) {
			placeSelectedLines.push(outcome);
		}
	});
	return placeSelectedLines;
});

const handleOutcomeSelection = (outcome: IOutcome) => {
	selectedMarketOutcome.value = outcome;
};

const selectedBets = computed(() => {
	if (!selectedMarketOutcome.value) return;

	betsSelectedOutcomes.value = props.selectedLines.filter(({ eventId }) => eventId !== selectedMarketOutcome.value?.eventId);
	betsSelectedOutcomes.value.push(selectedMarketOutcome.value);
	const payload = {
		league: jackpot.value.leagueId,
		jackpotDate: jackpot.value.date,
		bets: [...betsSelectedOutcomes.value.map(({ eventId, marketId, outcomeId }) => ({ eventId, marketId, outcomeId }))],
	};

	return payload;
});

const { mutate: strikeBet, isLoading: isStriking } = useMutation<IStrikeResponse>({
	mutationFn: () =>
		api({
			url: "/Betting/StrikeHorsesJackpotBets",
			type: "bettingHorses",
			apiVersion: "v2",
			options: {
				method: "POST",
				headers: {},
			},
			data: selectedBets.value,
		}),
	onSuccess: (response) => {
		emits("submit-confirmation", { response, betsOutcomes: betsSelectedOutcomes.value });
	},
	onError: (error) => {
		emits("submit-confirmation", error);
	},
});
</script>

<template>
	<UiModal
		:size="'small'"
		:active="active"
		@set-active="(val: boolean) => emits('set-active', val)"
		:title="t('select-horse-to-place')"
		:content-padding="false"
	>
		<div v-if="jackpotClosed" class="bg-light-50 dark:bg-dark-800">
			<div class="p-2">
				<div class="text-center text-xs font-bold">
					{{ t("jackpot-closed") }}
				</div>
				<div class="my-2 rounded-lg border border-light-500 dark:dark:border-dark-400 overflow-hidden">
					{{ t("jackpot-has-been-closed") }}
				</div>
			</div>
			<div class="bg-light-50 dark:bg-dark-900 flex justify-center">
				<UiButton @click="emits('submit-confirmation')" :label="t('close')" :size="'medium'" class="my-2 mx-4 w-full" />
			</div>
		</div>
		<div v-else class="bg-light-50 dark:bg-dark-800">
			<div class="p-2">
				<div class="text-center text-xs font-bold">
					{{ t("pick-horse-description") }}
				</div>
				<div
					v-for="outcome in placeOutcomes"
					:key="`pick-to-place-${outcome.outcomeId}`"
					class="my-2 rounded-lg border border-light-500 dark:dark:border-dark-400 overflow-hidden"
				>
					<Outcome
						@outcome-selected="handleOutcomeSelection($event)"
						:is-event-active="true"
						:outcome="outcome"
						:is-selected="selectedMarketOutcome?.displayName === outcome.displayName"
						class="rounded-md"
					/>
				</div>
			</div>
			<div class="bg-light-50 dark:bg-dark-900 flex justify-center">
				<UiButton
					:loading="isStriking"
					:disabled="!selectedMarketOutcome"
					@click="strikeBet()"
					:label="t('submit')"
					:size="'medium'"
					class="my-2 mx-4 w-full"
				/>
			</div>
		</div>
	</UiModal>
</template>
